import { BlurView } from 'expo-blur'
import { LinearGradient } from 'expo-linear-gradient'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useState } from 'react'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { Avatar } from 'react-native-paper'

import { Text } from '../../components'
import { GradientButton } from '../../components/GradientButton/GradientButton'
import { DS } from '../../design-system'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { color, fontFamily } from '../../theme'
import { SkeletonLoader } from './SkeletonLoader'
import styles from './styles'
import { useRemoteConfig } from '../../../module/remote-config/useRemoteConfig'
import { usePollingQuery } from '@/hooks/usePollingQuery'

const EMOJIS = ['👑', '👏', '🙌', '🔥', '💃🏻']

type UserWelcomeProps = {
  openDailyInspirationCommentSheet: () => void
  goToDiscussScreen: () => void
}

export const UserWelcome = observer(({ openDailyInspirationCommentSheet, goToDiscussScreen }: UserWelcomeProps) => {
  const {
    session: {
      user,
      firstName,
      isUserVisitingForFirstTime,
      dailyInspoReaction,
      reactOnDailyInspirationalQuote,
      getInspoReactionsUsingId,
      getDailyInspirationalQuote,
      dailyInspirationalQuoteData,
      getDailyTask,
      taskDay,
      totalResponse,
      userResponseCount,
      getUserResponseCount
    },
    analytics: { setEvent }
  } = useStore()
  const userId = user?._id.toString()
  const userInitial = firstName[0]
  const [isEmojiListVisible, setIsEmojiListVisible] = useState(false)
  const [isFetchingQuote, setIsFetchingQuote] = useState(true)
  const isWelcomeMessageEnabled = useRemoteConfig((state) => state.is_home_welcome_message_enabled)
  const isDailyInspirationEnabled = useRemoteConfig((state) => state.is_daily_inspiration_enabled)
  const isDailyDiscussionsEnabled = useRemoteConfig((state) => state.is_daily_discussions_enabled)

  const fetchMotivationalQuote = useCallback(async () => {
    try {
      await getDailyInspirationalQuote()
      getInspoReactionsUsingId()
      getDailyTask()
      getUserResponseCount()
    } catch (error) {
      console.log('fetchMotivationalQuote error', error)
    } finally {
      setIsFetchingQuote(false)
    }
  }, [getDailyInspirationalQuote, getDailyTask, getInspoReactionsUsingId])

  useEffect(() => {
    fetchMotivationalQuote()
  }, [fetchMotivationalQuote, taskDay])

  usePollingQuery('getInspoReactionsUsingId', getInspoReactionsUsingId, {
    refetchInterval: 30_000
  })

  const handleReaction = async (emoji: string) => {
    if (dailyInspirationalQuoteData?.acf) {
      setEvent(events.homescreen.motivation_engaged, {
        userId,
        content: dailyInspirationalQuoteData.acf.motivational_message,
        emoji_type: emoji,
        day: dailyInspirationalQuoteData.acf.day,
        week: dailyInspirationalQuoteData.acf.week
      })
    }
    setIsEmojiListVisible(false)
    await reactOnDailyInspirationalQuote(emoji)
  }

  return (
    <View style={{ overflow: 'visible' }}>
      {isWelcomeMessageEnabled && (
        <Text
          tx={isUserVisitingForFirstTime ? 'common.welcomeMsg' : 'common.welcomeBackMsg'}
          txOptions={{ name: firstName }}
          style={isUserVisitingForFirstTime ? styles.welcomeMsg : styles.welcomeBackMsg}
        />
      )}
      {isDailyInspirationEnabled && (
        <>
          <BlurView intensity={80} tint="light" style={startStyle.dailyInspoContainer}>
            <View style={startStyle.dailyInspoHeader}>
              <LinearGradient
                colors={['transparent', 'black']}
                start={[0, 0]}
                end={[1, 1]}
                style={startStyle.dailyInspoSideGradient}
              />
              <DS.Text variant="Mini/Bold" style={{ paddingHorizontal: 18 }}>
                DAILY INSPO
              </DS.Text>
              <LinearGradient
                colors={['black', 'transparent']}
                start={[0, 0]}
                end={[1, 1]}
                style={startStyle.dailyInspoSideGradient}
              />
            </View>
            {isFetchingQuote ? (
              <SkeletonLoader height={50} />
            ) : (
              <DS.Text
                variant="Heading 5/Regular"
                textAlign="center"
                style={{
                  marginTop: 12
                }}
              >
                {dailyInspirationalQuoteData?.acf?.motivational_message ?? ''}
              </DS.Text>
            )}
            {isDailyDiscussionsEnabled && dailyInspirationalQuoteData?.acf?.external_or_discussion_link ? (
              <React.Fragment key="share-your-thought">
                {userResponseCount < 1 ? (
                  <TouchableOpacity
                    style={startStyle.shareThoughtButton}
                    onPress={() => {
                      setEvent(events.dailyInspirationEvents.response_started, {
                        userId
                      })
                      openDailyInspirationCommentSheet()
                    }}
                  >
                    <Avatar.Text
                      size={24}
                      label={userInitial}
                      color={color.palette.black}
                      style={{ backgroundColor: color.palette.pink }}
                      labelStyle={{ fontFamily: fontFamily.GTAmericaExtended, fontSize: 12 }}
                    />
                    <DS.Text variant="Body Medium/Regular" style={{ paddingLeft: 10 }}>
                      Share your thoughts
                    </DS.Text>
                  </TouchableOpacity>
                ) : (
                  <GradientButton
                    title="Read everyone’s responses"
                    onPress={goToDiscussScreen}
                    contentContainerStyle={startStyle.submitGradientButton}
                  />
                )}
                {(totalResponse ?? 0) > 0 && (
                  <DS.Text textAlign="center" variant="Caption/Regular">
                    {`Respond to read replies from ${totalResponse} others!`}
                  </DS.Text>
                )}
              </React.Fragment>
            ) : null}
          </BlurView>
          {isEmojiListVisible && (
            <BlurView intensity={80} tint="light" style={startStyle.handRankings}>
              {EMOJIS.map((item, index) => {
                return (
                  <TouchableOpacity key={`${item}-${index}`} onPress={() => handleReaction(item)}>
                    <DS.Text variant="Heading 4/Medium">{item}</DS.Text>
                  </TouchableOpacity>
                )
              })}
            </BlurView>
          )}
          <View key="emojiListContainer" style={startStyle.emojiListContainer}>
            <TouchableOpacity onPress={() => setIsEmojiListVisible(!isEmojiListVisible)}>
              <Image source={require('./assets/emoji-icon.png')} style={startStyle.emojiIcon} />
            </TouchableOpacity>
            {dailyInspoReaction?.map((item) => {
              if (item.count > 0) {
                return (
                  <TouchableOpacity
                    onPress={() => handleReaction(item.emoji)}
                    key={`${item}`}
                    style={startStyle.reactedEmojiContainer}
                  >
                    <DS.Text variant="Body Small/Regular">{`${item.emoji} ${
                      item.count > 1 ? item.count : '\b'
                    }`}</DS.Text>
                  </TouchableOpacity>
                )
              }
              return null
            })}
          </View>
        </>
      )}
    </View>
  )
})

const startStyle = StyleSheet.create({
  handRankings: {
    marginTop: 16,
    paddingVertical: 16,
    paddingHorizontal: 12,
    gap: 8,
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    width: '80%',
    alignSelf: 'center',
    position: 'absolute',
    bottom: 20,
    zIndex: 2,
    overflow: 'hidden'
  },
  reactedEmojiContainer: {
    backgroundColor: DS.palette['Global/White'],
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 32,
    marginHorizontal: 5
  },
  emojiIcon: {
    width: 45,
    height: 45
  },
  emojiListContainer: {
    position: 'absolute',
    overflow: 'visible',
    flexDirection: 'row',
    bottom: -25,
    zIndex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  },
  dailyInspoHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  dailyInspoContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.50)',
    paddingTop: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    marginTop: 16,
    paddingBottom: 30,
    minHeight: 100,
    overflow: 'hidden'
  },
  discussButton: {
    marginTop: 8,
    alignSelf: 'center',
    width: '50%'
  },
  dailyInspoSideGradient: {
    width: 80,
    height: 2
  },
  userInitialContainer: {
    backgroundColor: color.palette.pink,
    justifyContent: 'center',
    alignItems: 'center',
    height: 32,
    width: 32,
    borderRadius: 16
  },
  shareThoughtButton: {
    flexDirection: 'row',
    borderRadius: 12,
    backgroundColor: color.palette.white,
    borderColor: color.palette.lightGrey,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    marginVertical: 12,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4
    },
    shadowOpacity: 0.05,
    shadowRadius: 12,
    elevation: 4,
    borderWidth: 1
  },
  submitGradientButton: {
    borderRadius: DS.spacing[24],
    paddingHorizontal: 16,
    height: 48,
    width: '90%',
    alignSelf: 'center',
    marginVertical: 12
  },
  submitButton: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  }
})
